/**
 * Published sites: typed client over the local `api` helper (plan 5.4, 7.10).
 *
 * Every response is the `{ data, error, meta }` envelope. The request
 * language goes along (`Accept-Language`) so the server's name messages and
 * plan walls come back in the app's language, and the device timezone
 * (`X-Timezone`) so the day of the deploy quota is the user's own day.
 * Thumbnails are 15-minute signed URLs: never persist them.
 */
import type {
  APIResponse,
  ArtifactVersionList,
  EntitlementDecision,
  PublishedSiteDetail,
  PublishedSiteList,
  PublishSiteRequest,
  SiteDeployList,
  SiteListQuery,
  SiteNameAvailability,
  SiteStats,
  UpdateSiteRequest,
} from '@tabbio/types';
import i18n from '@/i18n';
import { api } from './api';
import { resolveDeviceTimezone } from './device-timezone';

/** A handled sites failure: the code, the HTTP status and what the server attached. */
export class SitesApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number | null,
    readonly details: Record<string, unknown>,
    /** Set for a plan wall (`SUBSCRIPTION_ENTITLEMENT_DENIED`). */
    readonly decision: EntitlementDecision | null,
  ) {
    super(message);
    this.name = 'SitesApiError';
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** The envelope's error as a `SitesApiError`, with the details and the plan decision. */
export function sitesErrorFromEnvelope<T>(res: APIResponse<T>): SitesApiError | null {
  if (!res.error) return null;
  const meta = record(res.meta) ?? {};
  const errorDetails = record((res.error as { details?: unknown }).details) ?? {};
  const details = { ...errorDetails, ...(record(meta.details) ?? {}) };
  const status = typeof details.status === 'number' ? details.status : null;
  const decision = record(meta.decision) as EntitlementDecision | null;
  return new SitesApiError(
    res.error.message,
    res.error.code,
    status,
    details,
    decision,
  );
}

function unwrap<T>(res: APIResponse<T>): T {
  const error = sitesErrorFromEnvelope(res);
  if (error) throw error;
  if (res.data == null) {
    throw new SitesApiError('Empty response', 'EMPTY_RESPONSE', null, {}, null);
  }
  return res.data;
}

function headers(): Record<string, string> {
  const timezone = resolveDeviceTimezone();
  return {
    'Accept-Language': i18n.language || 'en',
    ...(timezone ? { 'X-Timezone': timezone } : {}),
  };
}

const sitePath = (id: string) => `/api/sites/${encodeURIComponent(id)}`;
const artifactPath = (id: string) => `/api/artifacts/${encodeURIComponent(id)}`;

/** GET /api/sites/name-availability?name=&artifactId= (cancellable). */
export async function checkSiteName(
  name: string,
  options: { artifactId?: string; signal?: AbortSignal } = {},
): Promise<SiteNameAvailability> {
  const query = new URLSearchParams({ name });
  if (options.artifactId) query.set('artifactId', options.artifactId);
  return unwrap(
    await api.get<SiteNameAvailability>(`/api/sites/name-availability?${query}`, {
      headers: headers(),
      signal: options.signal,
    }),
  );
}

/** GET /api/sites?tab=&q=&status=&cursor=&limit= */
export async function listSites(query: SiteListQuery = {}): Promise<PublishedSiteList> {
  const params = new URLSearchParams();
  if (query.tab) params.set('tab', query.tab);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.status) params.set('status', query.status);
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit) params.set('limit', String(query.limit));
  const suffix = params.toString();
  return unwrap(
    await api.get<PublishedSiteList>(`/api/sites${suffix ? `?${suffix}` : ''}`, {
      headers: headers(),
    }),
  );
}

/** GET /api/sites/:siteId */
export async function getSite(siteId: string): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.get<{ site: PublishedSiteDetail }>(sitePath(siteId), { headers: headers() }),
  );
  return data.site;
}

/** GET /api/artifacts/:id/site (null when the artifact was never published). */
export async function getArtifactSite(artifactId: string): Promise<PublishedSiteDetail | null> {
  const data = unwrap(
    await api.get<{ site: PublishedSiteDetail | null }>(`${artifactPath(artifactId)}/site`, {
      headers: headers(),
    }),
  );
  return data.site ?? null;
}

/** POST /api/artifacts/:id/publish (waits up to 10 s; the deploy may still be building). */
export async function publishArtifactSite(
  artifactId: string,
  body: PublishSiteRequest,
): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.post<{ site: PublishedSiteDetail }>(`${artifactPath(artifactId)}/publish`, body, {
      headers: headers(),
    }),
  );
  return data.site;
}

/** PATCH /api/sites/:siteId (a rename or a switch rebuilds a live site). */
export async function updateSite(
  siteId: string,
  body: UpdateSiteRequest,
): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.patch<{ site: PublishedSiteDetail }>(sitePath(siteId), body, { headers: headers() }),
  );
  return data.site;
}

/** POST /api/sites/:siteId/deploys (Update site; default the head). */
export async function redeploySite(
  siteId: string,
  versionId?: string,
): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.post<{ site: PublishedSiteDetail }>(
      `${sitePath(siteId)}/deploys`,
      versionId ? { versionId } : {},
      { headers: headers() },
    ),
  );
  return data.site;
}

/** POST /api/sites/:siteId/rollback */
export async function rollbackSite(siteId: string, deployId: string): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.post<{ site: PublishedSiteDetail }>(
      `${sitePath(siteId)}/rollback`,
      { deployId },
      { headers: headers() },
    ),
  );
  return data.site;
}

/** DELETE /api/sites/:siteId (unpublish: the address answers 410 at once). */
export async function unpublishSite(siteId: string): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.delete<{ site: PublishedSiteDetail }>(sitePath(siteId), { headers: headers() }),
  );
  return data.site;
}

/** POST /api/sites/:siteId/delete (the Danger zone). */
export async function deleteSite(
  siteId: string,
  keepArtifact: boolean,
): Promise<{ deleted: true; artifactDeleted: boolean }> {
  return unwrap(
    await api.post<{ deleted: true; artifactDeleted: boolean }>(
      `${sitePath(siteId)}/delete`,
      { keepArtifact },
      { headers: headers() },
    ),
  );
}

/** GET /api/sites/:siteId/stats?days= (UTC days, zero-filled). */
export async function getSiteStats(siteId: string, days = 30): Promise<SiteStats> {
  return unwrap(
    await api.get<SiteStats>(`${sitePath(siteId)}/stats?days=${days}`, { headers: headers() }),
  );
}

/** GET /api/sites/:siteId/deploys (site history, newest first). */
export async function getSiteDeploys(siteId: string): Promise<SiteDeployList> {
  return unwrap(
    await api.get<SiteDeployList>(`${sitePath(siteId)}/deploys`, { headers: headers() }),
  );
}

/** POST /api/sites/drafts: the draft site of a page made in the composer's Sites mode. */
export async function createSiteDraft(body: {
  artifactId: string;
  threadId?: string;
}): Promise<PublishedSiteDetail> {
  const data = unwrap(
    await api.post<{ site: PublishedSiteDetail }>('/api/sites/drafts', body, { headers: headers() }),
  );
  return data.site;
}

/**
 * The head version's blocking findings count, from the newest version row
 * (`GET /api/artifacts/:id/versions?limit=1`). Zero when the API predates
 * versions or has no head yet.
 */
export async function getArtifactHeadCheckErrors(artifactId: string): Promise<number> {
  const res = await api.get<ArtifactVersionList>(`${artifactPath(artifactId)}/versions?limit=1`);
  if (res.error || !res.data) return 0;
  const head = res.data.versions.find((version) => version.isCurrent) ?? res.data.versions[0];
  return head?.checks?.errors ?? 0;
}
